import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import styled from "styled-components";
import { ProgrammeSummaryStrip } from "../../../library/ProgrammeSummaryStrip";
import { usePermissionGuardRedirect } from "../../../../hooks/usePermissionGuard";
import { invalidateVersionListQueries, usePmcVersionListForFilters, useVersionNotesAudit } from "../../../../query/hooks/QueryHooksLinkedProgrammes";
import { useProgrammeSummary } from "../../../../query/hooks/QueryHooksProgramDetail";
import { tokens } from "../../../../public/tokens";
import { deleteVersion } from "../../../../services/ProgramService";
import PermissionsGate from "../../../../utils/permissionGate";
import ConfirmationModal from "../linkedProgrammes/ConfirmationModal";
import VersionCardsGrid from "./versionCards/VersionCardsGrid";

/*
 * No max-width/centering here - this page uses the full main-content width (the AMI
 * shell already applies the 24px page padding around it), matching the Version Details/
 * Modules screens. The card grid below (VersionCardsGrid.js) is a responsive 3/2/1-column
 * CSS grid, so a wide viewport just fits more equal-width columns rather than stretching
 * a single card or leaving a gutter.
 */
const StyledPage = styled.div({
    width: "100%",
    minWidth: 0,
});

const StyledCardsWrapper = styled.div({
    marginTop: tokens.globals["spacing-6"],
});

/**
 * Programme > Versions list (SPEC1): the active-version summary plus one
 * card per version, replacing the old single-row table. Cards only — no
 * search/filter/export/pagination, per the AP-1217 spec.
 * @param {string | number} id Programme id, passed down by the caller.
 */
function VersionsList({ id: programmeId }) {
    const history = useHistory();
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    usePermissionGuardRedirect(["pmcversionview"]);

    const {
        data: versions,
        isLoading,
        isError,
        refetch,
    } = usePmcVersionListForFilters(programmeId);

    const {
        data: summary,
        isLoading: isSummaryLoading,
    } = useProgrammeSummary(programmeId);

    const { data: notesAudit } = useVersionNotesAudit(programmeId);

    const activeVersion = versions.find((version) => Number(version.id) === Number(version.version_id));

    const status = isLoading ? "loading" : isError ? "error" : "ready";

    const noVersionMessage = status === "loading"
        ? "Loading…"
        : status === "error"
            ? "Unavailable"
            : versions.length ? "No active version" : "No versions";

    const openCreateVersion = () => {
        history.push(`/program-management-center/programmes/versions/create?programmeId=${programmeId}`);
    };

    const openActiveVersionDetails = () => {
        history.push(`/program-management-center/programmes/versions/details?versionId=${activeVersion.id}&programmeId=${programmeId}`);
    };

    const handleDeleteRequest = (versionId, versionName, isActive) => {
        if (isActive) {
            Swal.fire({ icon: "warning", text: "You cannot delete the active PMC version." });
            return;
        }

        if (versions.length === 1) {
            Swal.fire({ icon: "warning", text: "You cannot delete the only remaining PMC version." });
            return;
        }

        setDeleteTarget({ id: versionId, name: versionName });
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) {
            return;
        }

        setIsDeleting(true);

        try {
            await deleteVersion({ id: deleteTarget.id });
            await invalidateVersionListQueries();
            setDeleteTarget(null);
            Swal.fire("Deleted!", "Version has been removed.", "success");
        } catch (error) {
            console.error("VersionsList.js->handleConfirmDelete->Error", error);
            Swal.fire({ icon: "error", text: "Something went wrong while deleting this version. Please try again." });
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <StyledPage>
            <PermissionsGate
                scopes={["pmcversionadd"]}
                errorProps={{
                    createVersionDisabled: true,
                    createVersionDisabledReason: "Insufficient Permissions",
                    actionDisabled: true,
                    actionDisabledReason: "Insufficient Permissions",
                }}
            >
                {status !== "ready" || !activeVersion ? (
                    <ProgrammeSummaryStrip
                        hasVersion={false}
                        noVersionMessage={noVersionMessage}
                        onCreateVersion={status === "ready" ? openCreateVersion : undefined}
                    />
                ) : (
                    <ProgrammeSummaryStrip
                        versionName={activeVersion.name}
                        isActive
                        isLocked={Boolean(activeVersion.lock_version_settings)}
                        coordinator={activeVersion.coordinator}
                        onAssign={openActiveVersionDetails}
                        totals={summary}
                        isLoading={isSummaryLoading}
                        onAction={openCreateVersion}
                        actionLabel="Create version"
                        actionIcon="plus"
                    />
                )}
            </PermissionsGate>

            <StyledCardsWrapper>
                <VersionCardsGrid
                    status={status}
                    versions={versions}
                    programmeId={programmeId}
                    notesAudit={notesAudit}
                    onRetry={refetch}
                    onDelete={handleDeleteRequest}
                />
            </StyledCardsWrapper>

            <ConfirmationModal
                open={Boolean(deleteTarget)}
                copy={
                    deleteTarget && {
                        title: `Delete ${deleteTarget.name}?`,
                        description: "This version will be permanently removed. This can't be undone.",
                        label: "Delete",
                        icon: "trash-alt",
                        tone: "destructive",
                    }
                }
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleConfirmDelete}
                primaryDisabled={isDeleting}
            />
        </StyledPage>
    );
}

export default VersionsList;
